import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

import { scrollToTop } from '@/lib/scroll';

/**
 * Scroll offsets keyed by history entry (`location.key`).
 *
 * Module scope rather than a ref so the map survives remounts of this
 * component; it is intentionally per-page-load state and is dropped on reload.
 */
const scrollPositions = new Map<string, number>();

/**
 * Restores the scrolling behaviour that client-side routing takes away: a new
 * page starts at the top, and going back returns you to where you were.
 *
 * The app uses `BrowserRouter` + `useRoutes` (not a data router), so React
 * Router's `<ScrollRestoration />` is unavailable and this is handled by hand.
 *
 * Behaviour:
 * - Navigating to a different path scrolls to the top.
 * - Back/forward restores the offset recorded for that history entry.
 * - Hash links are left alone so in-page anchors still work.
 * - Same-path navigations that only change the query string are left alone, so
 *   URL-synced filters and the `/search` "load more" flow don't yank the page.
 *
 * Renders nothing. Must be mounted inside the router.
 */
export function ScrollToTop() {
  const { pathname, hash, key } = useLocation();
  const navigationType = useNavigationType();

  const previousPathnameRef = useRef(pathname);

  // Take over scroll handling from the browser, which cannot restore a
  // position for content that React has not rendered yet.
  useEffect(() => {
    if (!('scrollRestoration' in window.history)) return;

    const previous = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';
    return () => {
      window.history.scrollRestoration = previous;
    };
  }, []);

  // Track the current offset for the active history entry, coalesced to one
  // write per frame so the listener stays cheap. Re-registered per entry so
  // `key` is captured in the closure instead of read from a ref.
  useEffect(() => {
    let frame = 0;

    const handleScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        scrollPositions.set(key, window.scrollY);
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [key]);

  useEffect(() => {
    const pathnameChanged = previousPathnameRef.current !== pathname;
    previousPathnameRef.current = pathname;

    // `#section` links own the scroll position.
    if (hash) return;

    if (navigationType === 'POP') {
      const savedOffset = scrollPositions.get(key);

      if (savedOffset !== undefined) {
        // Route components are lazy-loaded, so the document may still be short
        // when this runs. Waiting a frame lets the page lay out first.
        const frame = window.requestAnimationFrame(() => {
          window.scrollTo({ top: savedOffset, left: 0, behavior: 'auto' });
        });
        return () => window.cancelAnimationFrame(frame);
      }
    }

    if (!pathnameChanged) return;

    scrollToTop();
  }, [pathname, hash, key, navigationType]);

  return null;
}
